import React, { useMemo } from 'react';
import { Badge, Card, CardBody, DataTable, Button, Icon, Checkbox } from '@aidkitorg/component-library';
import { BreadcrumbProps } from '@aidkitorg/component-library';
import { ColumnDef } from '@tanstack/react-table';
import { AdminPanelPage } from '../AdminPanelPage';
import { useLocalizedStrings } from '../../Localization';
import { InvoiceFilters } from './components/filters/InvoiceFilters';
import { useInvoicesContext, Invoice } from '../../Contexts/InvoicesContext';

export const InvoicesPage = () => {
  const L = useLocalizedStrings();
  const { 
    filteredInvoices, 
    isLoading, 
    selectedRows, 
    setFilters, 
    toggleRowSelection, 
    toggleAllRows 
  } = useInvoicesContext();

  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: L.admin_panel.nav.accounting,
    },
    {
      label: L.admin_panel.nav.invoices,
    },
  ];

  const selectedCount = selectedRows.size;

  const getStatusBadge = (status: Invoice['status']) => {
    switch (status) {
      case 'Unpaid':
        return <Badge variant='error'>{L.admin_panel.invoices.status_unpaid}</Badge>;
      case 'Overdue':
        return <Badge variant='error'>{L.admin_panel.invoices.status_overdue}</Badge>;
      case 'Paid':
        return <Badge variant='success'>{L.admin_panel.invoices.status_paid}</Badge>;
      case 'Partially Paid':
        return <Badge variant='info'>{L.admin_panel.invoices.status_partially_paid}</Badge>;
      case 'Partially Credited':
        return <Badge variant='warning'>{L.admin_panel.invoices.status_partially_credited}</Badge>;
      case 'Pending':
        return <Badge variant='warning'>{L.admin_panel.invoices.status_pending}</Badge>;
      case 'Credited':
        return <Badge variant='info'>{L.admin_panel.invoices.status_credited}</Badge>;
      default:
        return <Badge variant='neutral'>{status}</Badge>;
    }
  };

  const handleDownload = (invoice: Invoice) => {
    console.log('Downloading invoice:', invoice.invoice_number);
  };

  const handleReminder = (invoice: Invoice) => {
    console.log('Sending reminder for invoice:', invoice.invoice_number);
  };

  const handleBulkDownload = () => {
    const selected = filteredInvoices.filter(inv => selectedRows.has(inv.id));
    console.log('Bulk downloading invoices:', selected.map(inv => inv.invoice_number));
  };

  const columns: ColumnDef<Invoice>[] = useMemo(() => [
    {
      id: 'select',
      header: () => (
        <Checkbox
          label=""
          checked={selectedRows.size === filteredInvoices.length && filteredInvoices.length > 0}
          onChange={toggleAllRows}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          label=""
          checked={selectedRows.has(row.original.id)}
          onChange={() => toggleRowSelection(row.original.id)}
        />
      ),
      enableSorting: false,
    },
    {
      header: L.admin_panel.invoices.invoice_id.toUpperCase(),
      accessorKey: 'invoice_number',
      cell: ({ row }) => (
        <a 
          href="#" 
          className="text-blue-600 hover:text-blue-800 font-medium"
          onClick={(e) => {
            e.preventDefault();
            console.log('Navigate to invoice:', row.original.invoice_number);
          }}
        >
          {row.original.invoice_number}
        </a>
      ),
    },
    {
      header: L.admin_panel.invoices.issue_date.toUpperCase(),
      accessorKey: 'invoice_date',
      cell: ({ row }) => {
        if (!row.original.invoice_date) return '--';
        const date = new Date(row.original.invoice_date);
        if (isNaN(date.getTime())) return '--';
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      },
    },
    {
      header: L.admin_panel.invoices.due_date.toUpperCase(),
      accessorKey: 'due_date',
      cell: ({ row }) => {
        if (!row.original.due_date) return '--';
        const date = new Date(row.original.due_date);
        if (isNaN(date.getTime())) return '--';
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      },
    },
    {
      header: L.admin_panel.invoices.total.toUpperCase(),
      accessorKey: 'total_amount',
      cell: ({ row }) => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(row.original.total_amount);
      },
    },
    {
      header: L.admin_panel.invoices.status.toUpperCase(),
      accessorKey: 'status',
      cell: ({ row }) => getStatusBadge(row.original.status),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const showReminder = row.original.status === 'Unpaid' || 
                           row.original.status === 'Overdue' || 
                           row.original.status === 'Pending' ||
                           row.original.status === 'Partially Paid';
        
        return (
          <div className="flex items-center gap-3">
            {showReminder && (
              <button
                onClick={() => handleReminder(row.original)}
                className="text-gray-600 hover:text-gray-900"
                title={L.admin_panel.invoices.send_reminder}
              >
                <Icon name="EnvelopeIcon" className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={() => handleDownload(row.original)}
              className="text-gray-600 hover:text-gray-900"
              title={L.admin_panel.invoices.download_pdf}
            >
              <Icon name="ArrowDownTrayIcon" className="w-5 h-5" />
            </button>
          </div>
        );
      },
      enableSorting: false,
    },
  ], [selectedRows, filteredInvoices.length, toggleAllRows, toggleRowSelection, L]);

  const invoicesEmpty = filteredInvoices.length === 0;
  const invoicesEmptyState = (
    <div className="py-12 text-center text-gray-600">
      <div className="text-lg font-medium text-gray-700 mb-2">
        {L.admin_panel.invoices.no_invoices_found}
      </div>
      <div className="text-sm">
        {L.admin_panel.invoices.no_invoices_available}
      </div>
    </div>
  );

  return (
    <AdminPanelPage
      title={L.admin_panel.nav.invoices}
      breadcrumbs={breadcrumbs}
    >
      <div className="w-full space-y-4">
        {/* Header with invoice count and bulk download button */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {filteredInvoices.length === 1 
              ? L.admin_panel.invoices.invoice_count.replace('{count}', filteredInvoices.length.toString())
              : L.admin_panel.invoices.invoice_count_plural.replace('{count}', filteredInvoices.length.toString())
            }
          </div>
          <Button
            variant="secondary"
            onClick={handleBulkDownload}
            disabled={selectedCount === 0}
          >
            <div className="flex items-center gap-2">
              <Icon name="ArrowDownTrayIcon" className="w-4 h-4" />
              {L.admin_panel.invoices.download_selected.replace('{count}', selectedCount.toString())}
            </div>
          </Button>
        </div>

        <Card>
          <CardBody>
            {isLoading ? (
              <div className="py-12 text-center text-gray-600">
                <div className="text-lg font-medium text-gray-700 mb-2">
                  Loading invoices...
                </div>
              </div>
            ) : invoicesEmpty ? invoicesEmptyState : (
              <DataTable
                columns={columns}
                data={filteredInvoices}
                showPagination
                pageSize={10}
                initialSorting={[{ id: 'invoice_date', desc: true }]}
                enableSearch
                searchLabel={L.admin_panel.invoices.search_placeholder}
                getRowId={(row) => row.id}
                filters={(table) => (
                  <InvoiceFilters onFiltersChange={setFilters} />
                )}
              />
            )}
          </CardBody>
        </Card>
      </div>
    </AdminPanelPage>
  );
};
